import React, { useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { getYearlyData } from "../../../actions/earningAction";
import Loader from "../../Layouts/Loader";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { HiDownload } from "react-icons/hi";

const YearlyData = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.yearly);

  useEffect(() => {
    dispatch(getYearlyData());
  }, [dispatch]);

  // useEffect(() => {
  //   if (error) {
  //     toast.error(error);
  //     dispatch(clearErrors());
  //   }
  // }, [error, dispatch]);

  // Check if data is loading or if there is an error
  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  // if (!data || !data.yearlyIncome) {
  //     return <div>No data available</div>;
  // }

  // Download Excel
  const downloadExcel = () => {
    try {
      // Ensure data is valid and contains the expected values
      if (!data.yearlyIncome || !Array.isArray(data.yearlyIncome)) {
        throw new Error("Invalid data for Excel export.");
      }

      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Yearly Income Data");

      // Add header row with bold style
      const headerRow = worksheet.addRow(["Year", "Total Income"]);
      headerRow.font = { bold: true }; // Make the header bold

      // Center align header cells
      worksheet.columns.forEach((col) => {
        col.alignment = { horizontal: "center" };
      });

      // Add the data rows
      data.yearlyIncome.forEach((row) => {
        worksheet.addRow([
          row.year,
          `₹${new Intl.NumberFormat("en-IN").format(row.totalIncome || 0)}`,
        ]);
      });

      // Calculate the subtotal of totalIncome
      const totalIncomeSubtotal = data.yearlyIncome.reduce(
        (acc, row) => acc + (row.totalIncome || 0),
        0
      );

      // Add a row for the subtotal
      const subtotalRow = worksheet.addRow([
        "Subtotal",
        `₹${new Intl.NumberFormat("en-IN").format(totalIncomeSubtotal)}`,
      ]);

      // Style the subtotal row
      subtotalRow.font = { bold: true }; // Make the subtotal row bold
      subtotalRow.alignment = { horizontal: "center" }; // Center-align the subtotal row

      // Adjust column widths for better visibility
      worksheet.columns.forEach((col) => {
        let maxLength = 0;
        col.eachCell({ includeEmpty: true }, (cell) => {
          const length = cell.value ? cell.value.toString().length : 0;
          maxLength = Math.max(maxLength, length);
        });
        col.width = maxLength + 2; // Adding some padding
      });

      // Generate the Excel file and trigger download
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "yearly_income_data.xlsx");
        link.click();

        // Clean up URL to release memory
        URL.revokeObjectURL(url);
      });
    } catch (err) {
      toast.error(
        err.message || "An error occurred while downloading the Excel file."
      );
    }
  };

  return (
    <>
      {/* Yearly Charts Section */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6 shadow-sm">
        <h2 className="text-lg lg:text-xl font-semibold text-neutral-800 mb-6">
          Yearly Performance
        </h2>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data.yearlyIncome}
            margin={{ top: 0, right: 30, left: 10, bottom: 30 }}
            barSize={20}
          >
            <XAxis dataKey="year" padding={{ left: 10, right: 10 }} />
            <YAxis />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            />
            <Legend verticalAlign="top" height={36} />
            <CartesianGrid strokeDasharray="3 3" />
            <Bar
              dataKey="totalIncome"
              fill="#6366f1"
              background={{ fill: "#f3f4f6" }}
            />
          </BarChart>
        </ResponsiveContainer>

        <div className="flex justify-end mt-4">
          <button
            onClick={downloadExcel}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            Download Excel
            <HiDownload className="ml-2" />
          </button>
        </div>
      </div>
    </>
  );
};

export default YearlyData;
